'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, Suspense, useCallback, useState } from 'react';
import Link from 'next/link';
import { useHoverIntent } from 'react-use-hoverintent';
import { usePathname, useRouter } from 'next/navigation';
import { signIn, signOut, useSession } from 'next-auth/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHiveUserLight } from '@awesome.me/kit-94e593bf23/icons/kit/custom';
import { faBox } from '@awesome.me/kit-94e593bf23/icons/sharp/light';
import { faSpinnerThird } from '@awesome.me/kit-94e593bf23/icons/classic/light';
import { useAppContext } from '@/app/_lib/_context';
import { useGlobalMessage } from '@/app/_lib/hooks';
import signOutAction from '@/app/_lib/actions/SignOutAction';
import getAccountPreviewAction from '@/app/_lib/actions/GetAccountPreviewAction';
const AccountPreviewLoading = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="p-4 w-[320px] flex items-center justify-center"><FontAwesomeIcon icon={faSpinnerThird} className="animate-spin text-xl text-light" /></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
const AccountPreview = t0 => {
  const $ = _c(48);
  const {
    customer,
    sessionStatus,
    onClose,
    onSignOut,
    onOpenSignIn
  } = t0;
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = async e => {
      e.preventDefault();
      e.stopPropagation();
      setIsGoogleLoading(true);
      ;
      try {
        await signIn("google", {
          callbackUrl: "/account"
        });
      } catch (t2) {
        const error = t2;
        console.error("Google sign-in error:", error);
        setIsGoogleLoading(false);
      }
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const handleGoogleSignIn = t1;
  if (sessionStatus === "loading" || sessionStatus === "authenticated" && !customer) {
    let t2;
    if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <AccountPreviewLoading />;
      $[1] = t2;
    } else {
      t2 = $[1];
    }
    return t2;
  }
  if (sessionStatus === "unauthenticated") {
    let t2;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <span className="text-light text-sm block mb-4">Sign in to view your account</span>;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    let t3;
    if ($[3] !== onOpenSignIn) {
      t3 = <button onClick={onOpenSignIn} className="block w-full text-center bg-gray-5 hover:bg-primary text-ink hover:text-white text-sm font-medium py-2 px-4 rounded transition-colors">Sign In with Email</button>;
      $[3] = onOpenSignIn;
      $[4] = t3;
    } else {
      t3 = $[4];
    }
    let t4;
    if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-300" /></div>;
      $[5] = t4;
    } else {
      t4 = $[5];
    }
    let t5;
    if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <div className="relative my-4">{t4}<div className="relative flex justify-center text-xs"><span className="px-2 bg-white text-gray-500">or</span></div></div>;
      $[6] = t5;
    } else {
      t5 = $[6];
    }
    let t6;
    if ($[7] !== isGoogleLoading) {
      t6 = isGoogleLoading ? <><FontAwesomeIcon icon={faSpinnerThird} className="animate-spin" /><span>Connecting...</span></> : <><svg className="w-4 h-4" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" /><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" /><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" /><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" /></svg><span>Sign in with Google</span></>;
      $[7] = isGoogleLoading;
      $[8] = t6;
    } else {
      t6 = $[8];
    }
    let t7;
    if ($[9] !== isGoogleLoading || $[10] !== t6) {
      t7 = <button onClick={handleGoogleSignIn} disabled={isGoogleLoading} type="button" className="w-full flex items-center justify-center gap-2 px-4 py-2 border border-gray-10 rounded text-sm font-medium text-ink bg-white hover:border-primary hover:text-primary disabled:opacity-50 disabled:cursor-not-allowed transition-colors">{t6}</button>;
      $[9] = isGoogleLoading;
      $[10] = t6;
      $[11] = t7;
    } else {
      t7 = $[11];
    }
    let t8;
    if ($[12] !== onClose) {
      t8 = <span className="text-xs text-light block mt-4">New customer? <Link href="/account/create" className="text-primary hover:underline" onClick={onClose}>Create account</Link></span>;
      $[12] = onClose;
      $[13] = t8;
    } else {
      t8 = $[13];
    }
    let t9;
    if ($[14] !== t3 || $[15] !== t7 || $[16] !== t8) {
      t9 = <div className="p-4 w-[320px] text-left">{t2}{t3}{t5}{t7}{t8}</div>;
      $[14] = t3;
      $[15] = t7;
      $[16] = t8;
      $[17] = t9;
    } else {
      t9 = $[17];
    }
    return t9;
  }
  let t2;
  if ($[18] !== onSignOut) {
    t2 = <button onClick={onSignOut} className="absolute top-2 right-3 text-3xs text-light hover:text-primary transition-colors">Sign Out</button>;
    $[18] = onSignOut;
    $[19] = t2;
  } else {
    t2 = $[19];
  }
  const t3 = customer.first_name || "";
  let t4;
  if ($[20] !== t3) {
    t4 = <span className="text-sm font-medium text-ink-dark leading-tight">Welcome {t3}</span>;
    $[20] = t3;
    $[21] = t4;
  } else {
    t4 = $[21];
  }
  let t5;
  if ($[22] !== customer.account_id) {
    t5 = <span className="text-[11px] text-light leading-tight">{customer.account_id}</span>;
    $[22] = customer.account_id;
    $[23] = t5;
  } else {
    t5 = $[23];
  }
  let t6;
  if ($[24] !== t4 || $[25] !== t5) {
    t6 = <div className="flex flex-col px-4 py-2 border-b border-gray-5">{t4}{t5}</div>;
    $[24] = t4;
    $[25] = t5;
    $[26] = t6;
  } else {
    t6 = $[26];
  }
  let t7;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <span className="font-semibold">Orders</span>;
    $[27] = t7;
  } else {
    t7 = $[27];
  }
  const t8 = customer.order_count || 0;
  let t9;
  if ($[28] !== t8) {
    t9 = <span className="text-sm text-ink">{t7} ({t8})</span>;
    $[28] = t8;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  let t10;
  if ($[30] !== onClose) {
    t10 = <Link href="/account/order-history" className="text-3xs text-light hover:text-primary transition-colors" onClick={onClose}>View Orders</Link>;
    $[30] = onClose;
    $[31] = t10;
  } else {
    t10 = $[31];
  }
  let t11;
  if ($[32] !== t10 || $[33] !== t9) {
    t11 = <div className="flex items-center justify-between">{t9}{t10}</div>;
    $[32] = t10;
    $[33] = t9;
    $[34] = t11;
  } else {
    t11 = $[34];
  }
  let t12;
  if ($[35] !== customer.recent_order || $[36] !== onClose) {
    t12 = customer.recent_order ? <Link href={`/account/order-history/${encodeURIComponent(customer.recent_order.order_code)}`} className="flex items-center gap-2 text-xs text-light hover:text-primary transition-colors mt-3 outline-none ring-0 focus:outline-none focus:ring-0 focus-visible:text-primary" onClick={onClose}><FontAwesomeIcon icon={faBox} className="text-sm" aria-hidden="true" /><span>Most recent: #{customer.recent_order.order_id}</span></Link> : <span className="text-xs text-light block mt-3">No orders yet</span>;
    $[35] = customer.recent_order;
    $[36] = onClose;
    $[37] = t12;
  } else {
    t12 = $[37];
  }
  let t13;
  if ($[38] !== t11 || $[39] !== t12) {
    t13 = <div className="px-4 pt-2 pb-4 border-b border-gray-5">{t11}{t12}</div>;
    $[38] = t11;
    $[39] = t12;
    $[40] = t13;
  } else {
    t13 = $[40];
  }
  let t14;
  if ($[41] !== onClose) {
    t14 = <div className="px-4 py-3 bg-gray-1 rounded-b-lg"><Link href="/account" className="block w-full text-center bg-gray-5 hover:bg-primary text-ink hover:text-white text-sm font-medium py-2 px-4 rounded transition-colors" onClick={onClose}>My Account</Link></div>;
    $[41] = onClose;
    $[42] = t14;
  } else {
    t14 = $[42];
  }
  let t15;
  if ($[43] !== t13 || $[44] !== t14 || $[45] !== t2 || $[46] !== t6) {
    t15 = <div className="w-[320px] text-left relative">{t2}{t6}{t13}{t14}</div>;
    $[43] = t13;
    $[44] = t14;
    $[45] = t2;
    $[46] = t6;
    $[47] = t15;
  } else {
    t15 = $[47];
  }
  return t15;
};
const HeaderAccountIcon = t0 => {
  const $ = _c(49);
  const {
    closeMobileMenu,
    inMobileNav: t1,
    className
  } = t0;
  const inMobileNav = t1 === undefined ? false : t1;
  const pathname = usePathname();
  const router = useRouter();
  const {
    data: session,
    status: sessionStatus
  } = useSession();
  const {
    openSignInModal
  } = useAppContext();
  const showToast = useGlobalMessage();
  const dialogRef = useRef(null);
  const openedViaKeyboard = useRef(false);
  const isAuthenticated = !!session;
  const [accountPreview, setAccountPreview] = useState(null);
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      sensitivity: 6,
      interval: 100,
      timeout: 300
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const [isIntentHover, intentRef, setIsIntentHover] = useHoverIntent(t2);
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      setAccountPreview(null);
    };
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== sessionStatus) {
    t4 = [sessionStatus];
    $[2] = sessionStatus;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[4] !== accountPreview || $[5] !== isAuthenticated || $[6] !== isIntentHover) {
    t5 = () => {
      if (isIntentHover && isAuthenticated && !accountPreview) {
        const fetchPreview = async () => {
          const response = await getAccountPreviewAction();
          if (response.success) {
            setAccountPreview(response.preview);
          }
        };
        fetchPreview();
      }
    };
    t6 = [isIntentHover, isAuthenticated, accountPreview];
    $[4] = accountPreview;
    $[5] = isAuthenticated;
    $[6] = isIntentHover;
    $[7] = t5;
    $[8] = t6;
  } else {
    t5 = $[7];
    t6 = $[8];
  }
  useEffect(t5, t6);
  let t7;
  if ($[9] !== closeMobileMenu || $[10] !== setIsIntentHover) {
    t7 = () => {
      if (closeMobileMenu) {
        closeMobileMenu();
      }
      setIsIntentHover(false);
    };
    $[9] = closeMobileMenu;
    $[10] = setIsIntentHover;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const handleIconClick = t7;
  let t8;
  if ($[12] !== setIsIntentHover) {
    t8 = () => {
      setIsIntentHover(false);
      openedViaKeyboard.current = false;
    };
    $[12] = setIsIntentHover;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const handleClosePreview = t8;
  let t9;
  if ($[14] !== pathname || $[15] !== router || $[16] !== setIsIntentHover || $[17] !== showToast) {
    t9 = async () => {
      setIsIntentHover(false);
      const needsRedirect = pathname.startsWith("/checkout") || pathname.startsWith("/account");
      if (needsRedirect) {
        router.push("/");
      }
      await signOutAction();
      await signOut({
        redirect: false
      });
      showToast({
        message: "You have been signed out",
        messageType: "success"
      });
    };
    $[14] = pathname;
    $[15] = router;
    $[16] = setIsIntentHover;
    $[17] = showToast;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  const handleSignOut = t9;
  let t10;
  if ($[19] !== openSignInModal || $[20] !== setIsIntentHover) {
    t10 = () => {
      setIsIntentHover(false);
      openSignInModal();
    };
    $[19] = openSignInModal;
    $[20] = setIsIntentHover;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  const handleOpenSignIn = t10;
  let t11;
  if ($[22] !== isIntentHover || $[23] !== pathname) {
    t11 = isIntentHover && !pathname.startsWith("/account");
    $[22] = isIntentHover;
    $[23] = pathname;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  const showPreview = t11;
  let t12;
  let t13;
  if ($[25] !== setIsIntentHover || $[26] !== showPreview) {
    t12 = () => {
      if (!showPreview) {
        return;
      }
      const handleKeyDown = e => {
        if (e.key === "Escape") {
          setIsIntentHover(false);
          openedViaKeyboard.current = false;
        }
      };
      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    };
    t13 = [showPreview, setIsIntentHover];
    $[25] = setIsIntentHover;
    $[26] = showPreview;
    $[27] = t12;
    $[28] = t13;
  } else {
    t12 = $[27];
    t13 = $[28];
  }
  useEffect(t12, t13);
  let t14;
  if ($[29] !== setIsIntentHover) {
    t14 = () => {
      openedViaKeyboard.current = true;
      setIsIntentHover(true);
    };
    $[29] = setIsIntentHover;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  const handleFocus = t14;
  let t15;
  if ($[31] !== intentRef || $[32] !== setIsIntentHover) {
    t15 = event => {
      const container = intentRef.current;
      if (container && event.relatedTarget && container.contains(event.relatedTarget)) {
        return;
      }
      setIsIntentHover(false);
      openedViaKeyboard.current = false;
    };
    $[31] = intentRef;
    $[32] = setIsIntentHover;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  const handleBlur = t15;
  let t16;
  if ($[34] !== accountPreview || $[35] !== className || $[36] !== closeMobileMenu || $[37] !== handleBlur || $[38] !== handleClosePreview || $[39] !== handleFocus || $[40] !== handleIconClick || $[41] !== handleOpenSignIn || $[42] !== handleSignOut || $[43] !== inMobileNav || $[44] !== intentRef || $[45] !== isAuthenticated || $[46] !== sessionStatus || $[47] !== showPreview) {
    t16 = <>{inMobileNav ? <Link href={isAuthenticated ? "/account" : "/account/login"} aria-label={isAuthenticated ? "Your Account" : "Sign In"} className={`${className}`} onClick={closeMobileMenu}><Suspense>{isAuthenticated ? <span className="text-light text-lg me-2">Your Account</span> : <span className="text-light text-lg me-2">Sign In</span>}</Suspense><FontAwesomeIcon icon={faHiveUserLight} aria-hidden="true" className="text-2xl hover:text-primary" /></Link> : <div ref={intentRef} className={`relative ${className || ""}`} onBlur={handleBlur}><Link href={isAuthenticated ? "/account" : "/account/login"} aria-label={isAuthenticated ? "Your Account" : "Sign In"} aria-expanded={showPreview} aria-haspopup="dialog" onClick={handleIconClick} onFocus={handleFocus} className="relative block"><Suspense><FontAwesomeIcon icon={faHiveUserLight} aria-hidden="true" className="text-[1.75rem] lg:text-nav-icon hover:text-primary" /><div className={`${isAuthenticated ? "border-primary" : "hidden"} absolute bottom-[3px] left-0 right-0 mx-auto h-[4px] w-[12px] border-b-4 rounded`} /></Suspense></Link>{showPreview && <div ref={dialogRef} className="hidden lg:block absolute top-full right-0 pt-4 z-[100]" role="dialog" aria-label="Account preview"><div className="absolute top-2 right-1 w-0 h-0 border-l-8 border-r-8 border-b-8 border-l-transparent border-r-transparent border-b-white z-10" aria-hidden="true" /><div className="bg-white rounded-lg shadow-lg border border-gray-5 -mr-3"><AccountPreview customer={accountPreview} sessionStatus={sessionStatus} onClose={handleClosePreview} onSignOut={handleSignOut} onOpenSignIn={handleOpenSignIn} /></div></div>}</div>}</>;
    $[34] = accountPreview;
    $[35] = className;
    $[36] = closeMobileMenu;
    $[37] = handleBlur;
    $[38] = handleClosePreview;
    $[39] = handleFocus;
    $[40] = handleIconClick;
    $[41] = handleOpenSignIn;
    $[42] = handleSignOut;
    $[43] = inMobileNav;
    $[44] = intentRef;
    $[45] = isAuthenticated;
    $[46] = sessionStatus;
    $[47] = showPreview;
    $[48] = t16;
  } else {
    t16 = $[48];
  }
  return t16;
};
export default HeaderAccountIcon;